import { useEffect, useRef, useCallback } from "react";
import { Tooltip } from "antd";
import {
  BoldOutlined, ItalicOutlined, UnderlineOutlined, OrderedListOutlined, UnorderedListOutlined,
  AlignLeftOutlined, AlignCenterOutlined, AlignRightOutlined, LinkOutlined, UndoOutlined, RedoOutlined, BulbOutlined,
} from "@ant-design/icons";
import WidgetFrame from "./WidgetFrame";
import { previewWidget } from "./widgetPreview";

// A note line in the editor looks like the box it becomes in the mail.
const EDITOR_CSS = ".ew-message blockquote{margin:10px 0;padding:10px 14px;border-left:3px solid #5b2fd6;background:#f7f4ff;border-radius:0 8px 8px 0;}"
  + ".ew-message blockquote b,.ew-message blockquote strong{color:#5b2fd6;}";

/**
 * The message box for an email in a company design (2026-10-27). The internal user writes
 * normal text — bold, lists, alignment, links — and never sees HTML; the design's header and
 * footer stay fixed. `showPreview` adds the full email underneath (the inboxes, which have no
 * preview column of their own).
 */
const TOOLS = [
  [BoldOutlined, "bold", "Bold"], [ItalicOutlined, "italic", "Italic"], [UnderlineOutlined, "underline", "Underline"],
  null,
  [UnorderedListOutlined, "insertUnorderedList", "Bullet list"], [OrderedListOutlined, "insertOrderedList", "Numbered list"],
  null,
  [AlignLeftOutlined, "justifyLeft", "Align left"], [AlignCenterOutlined, "justifyCenter", "Align center"], [AlignRightOutlined, "justifyRight", "Align right"],
  null,
];

const toolBtn = {
  background: "none", border: "none", cursor: "pointer", padding: "4px 7px", borderRadius: 4,
  fontSize: 13, color: "#374151", display: "inline-flex", alignItems: "center",
};

export default function WidgetBodyEditor({ widget, value, onChange, subject, greeting, placeholder = "Write your message…", showPreview = false, minHeight = 220 }) {
  const ref = useRef(null);

  // Outside changes (a template loading, a reset) replace the text. The user's own typing comes
  // back as the very HTML already on screen, so the comparison leaves it (and the caret) alone.
  useEffect(() => {
    if (ref.current && ref.current.innerHTML !== (value || "")) ref.current.innerHTML = value || "";
  }, [value]);

  const emit = useCallback(() => {
    if (ref.current) onChange?.(ref.current.innerHTML);
  }, [onChange]);

  const exec = (cmd, val = null) => { ref.current?.focus(); document.execCommand(cmd, false, val); emit(); };

  const button = (Icon, cmd, title, val = null) => (
    <Tooltip title={title} key={cmd}>
      <button type="button" aria-label={title} style={toolBtn}
        onMouseDown={(e) => { e.preventDefault(); exec(cmd, val); }}
        onMouseEnter={(e) => { e.currentTarget.style.background = "#f3f4f6"; }}
        onMouseLeave={(e) => { e.currentTarget.style.background = "none"; }}>
        <Icon />
      </button>
    </Tooltip>
  );

  return (
    <div>
      <div style={{ border: "1px solid #d9d9d9", borderRadius: 10, overflow: "hidden", background: "#fff" }}>
        <div style={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 2, padding: "6px 10px", borderBottom: "1px solid #f0f0f0", background: "#fafafa" }}>
          {TOOLS.map((t, i) => (t
            ? button(t[0], t[1], t[2])
            : <div key={`sep-${i}`} style={{ width: 1, height: 18, background: "#e5e7eb", margin: "0 4px" }} />))}
          <Tooltip title="Insert link">
            <button type="button" aria-label="Insert link" style={toolBtn}
              onMouseDown={(e) => { e.preventDefault(); const url = window.prompt("Enter URL (https://…)"); if (url) exec("createLink", url); }}>
              <LinkOutlined />
            </button>
          </Tooltip>
          {/* The design's highlight box ("💡 No action needed: …"); pressed again, a plain line */}
          <Tooltip title="Note box — highlight this line">
            <button type="button" aria-label="Note box"
              style={{ ...toolBtn, gap: 4, fontSize: 12, fontWeight: 600, color: "#5b2fd6", background: "#f5f3ff", border: "1px solid #ddd6fe", marginLeft: 2 }}
              onMouseDown={(e) => {
                e.preventDefault();
                const inNote = String(document.queryCommandValue?.("formatBlock") || "").toLowerCase() === "blockquote";
                exec("formatBlock", inNote ? "div" : "blockquote");
              }}>
              <BulbOutlined /> Note
            </button>
          </Tooltip>
          <div style={{ width: 1, height: 18, background: "#e5e7eb", margin: "0 4px" }} />
          {button(UndoOutlined, "undo", "Undo")}
          {button(RedoOutlined, "redo", "Redo")}
          {widget && (
            <span style={{ marginLeft: "auto", fontSize: 11, fontWeight: 600, color: "#5b48e8", background: "#f5f3ff", border: "1px solid #ddd6fe", borderRadius: 20, padding: "2px 10px" }}>
              {widget.name}
            </span>
          )}
        </div>
        <style>{EDITOR_CSS}</style>
        <div ref={ref} className="ew-message" role="textbox" aria-label="Message" aria-multiline="true" contentEditable suppressContentEditableWarning
          onInput={emit} data-placeholder={placeholder}
          style={{ minHeight, padding: "12px 14px", outline: "none", fontSize: 13, lineHeight: 1.8, color: "#1a1a2e", overflowY: "auto" }} />
      </div>
      <div style={{ fontSize: 11.5, color: "#6b7280", marginTop: 6 }}>
        Just the message — the company header, greeting, sign-off and footer are added for you.
      </div>
      {showPreview && widget && (
        <div style={{ marginTop: 12, border: "1px solid #e5e7eb", borderRadius: 10, overflow: "hidden", maxHeight: 460, overflowY: "auto" }}>
          <WidgetFrame html={previewWidget(widget.html, value, subject, greeting)} title="Email preview" />
        </div>
      )}
    </div>
  );
}
